import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer, createTransform, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage"; // defaults to localStorage
import staffReducer from "../redux/universeStaffReducer.js";
import adminReducer from "../redux/admin_reducer.js";
import clientReducer, { sanitizeClientData } from "../redux/client_reducer.js";
import appFormReducer from "../redux/registrationReducer.js";
import terminalReducer from "../redux/terminal_reducer.js";
import ordersReducer from "../redux/orders_reducers.js";
import itemsReducer from "../redux/items_reducer.js";
import executiveReducer from "../redux/executive_reducer.js";
import financeReducer from "../redux/finance_reducer.js";
import agentReducer from "../redux/agent_reducer.js";
import systemAdminReducer from "../redux/system-admin_reducer.js";
import mongodbReducer from "../redux/mongodb.js";
import ceoReducer from "../../universe_ceo_source_file/ceoReducer.js";

// Combine reducers
const rootReducer = combineReducers({
  systemAdminFunction: systemAdminReducer,
  financeFunction: financeReducer,
  adminFunction: adminReducer,
  executiveFunction: executiveReducer,
  universeStaffs: staffReducer,
  clientFunction: clientReducer,
  agentFunction: agentReducer,
  applictaionForm: appFormReducer,
  terminalActions: terminalReducer,
  ordersActions: ordersReducer,
  itemsActions: itemsReducer,
  mongodbActions: mongodbReducer,
  universeCEO: ceoReducer,
});

// Only the issued session tokens are retained; account credentials never enter saved state.
const sanitizeStoredSlice = (state, key) => sanitizeClientData(
  state, key === "clientFunction" || key === "universeStaffs" || key === "universeCEO",
);
const credentialTransform = createTransform(sanitizeStoredSlice, sanitizeStoredSlice);
const sanitizePayload = () => next => action => {
  if (action.payload === undefined) return next(action);
  const payload = action.type === "persist/REHYDRATE" && action.payload
    ? Object.fromEntries(Object.entries(action.payload).filter(([key]) => persistedSlices.includes(key) || key === "_persist").map(([key, state]) => [key, sanitizeStoredSlice(state, key)]))
    : sanitizeClientData(action.payload, ["client_store/startclientAction", "staff_store/startStaffAction", "ceo_store/startCEOAction"].includes(action.type));
  return next({ ...action, payload });
};

const persistedSlices = Object.keys(rootReducer(undefined, { type: "@@INIT" }));

// Persist configuration
const persistConfig = {
  key: "root",
  storage,
  transforms: [credentialTransform],
  whitelist: persistedSlices,
};

// Create a persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Create store with persisted reducer
const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware => getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      // These inventory caches arrive in checked actions and are frozen by Immer.
      ignoredPaths: ["terminalActions.inventoryPOS.products", "ordersActions.warehouse.products"],
    },
    immutableCheck: {
      ignoredPaths: ["terminalActions.inventoryPOS.products", "ordersActions.warehouse.products"],
    },
  }).prepend(sanitizePayload),
});
const persistor = persistStore(store);

export { store, persistor };
